import { useMemo, useState } from "react";
import {
  useListAccessApiV1ManageDatasetIdShareGet,
  useSendInviteApiV1ManageDatasetIdInvitePut,
  useUpdateUserRoleApiV1ManageDatasetIdShareTargetUserIdRolePatch,
  useRevokeAccessApiV1ManageDatasetIdShareRevokeFromUserIdDelete,
} from "vl-api/vlComponents";
import type * as Schemas from "vl-api/vlSchemas";
import { App } from "antd";
import { useSelector } from "react-redux";
import { getDataset } from "../../../../redux/SingleDataset/selectors";

export const useDatasetShare = (datasetId: string) => {
  const { notification } = App.useApp();
  const [selectedRole, setSelectedRole] = useState<string>("editor");
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Schemas.User | null>(null);
  const dataset = useSelector(getDataset);

  const rolesList = [
    { value: "editor", label: "Editor" },
    { value: "viewer", label: "Viewer" },
  ];

  const { data: accessData, refetch } =
    useListAccessApiV1ManageDatasetIdShareGet({
      pathParams: { datasetId },
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by callers via return value
  const canManageAccess = accessData?.can_manage ?? false;

  const sendInviteMutation = useSendInviteApiV1ManageDatasetIdInvitePut();
  const updateUserRoleMutation =
    useUpdateUserRoleApiV1ManageDatasetIdShareTargetUserIdRolePatch();
  const revokeAccessMutation =
    useRevokeAccessApiV1ManageDatasetIdShareRevokeFromUserIdDelete();

  const isSubmitting = sendInviteMutation.isPending;
  const isRevoking = revokeAccessMutation.isPending;
  const isUpdating = updateUserRoleMutation.isPending;

  const owners = accessData?.members.filter((m) => m.role === "owner") || [];
  const collaborators =
    accessData?.members.filter((m) => m.role !== "owner") || [];

  const allUsers = useMemo(() => {
    return accessData?.available_users || [];
  }, [accessData]);

  const options = useMemo(() => {
    return allUsers.map((user) => ({
      value: user.user_id,
      label: user.name,
      username: user.username || "",
    }));
  }, [allUsers]);

  const handleAddUsers = async () => {
    if (!selectedUser) return;
    try {
      await sendInviteMutation.mutateAsync({
        pathParams: { datasetId },
        body: {
          grant_to_user_id: selectedUser.value,
          role: selectedRole,
        },
      } as any);

      notification.success({ message: "Users invited successfully" });

      // Reset selection
      setSelectedUser(null);
      setSelectedRole("editor");

      // Refresh the members list
      await refetch();
    } catch (error: any) {
      notification.error({
        message: error?.detail || error?.message || "Failed to invite users",
      });
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await revokeAccessMutation.mutateAsync({
        pathParams: {
          datasetId,
          revokeFromUserId: userId,
        },
      });

      notification.success({ message: "User access removed" });

      await refetch();
    } catch (error: any) {
      notification.error({
        message:
          error?.detail || error?.message || "Failed to remove user access",
      });
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: string) => {
    try {
      await updateUserRoleMutation.mutateAsync({
        pathParams: {
          datasetId,
          targetUserId: userId,
        },
        body: {
          role: newRole,
        },
      } as any);

      notification.success({ message: "User role updated" });
      await refetch();
    } catch (error: any) {
      notification.error({
        message:
          error?.detail || error?.message || "Failed to update user role",
      });
    }
  };

  return {
    selectedRole,
    setSelectedRole,
    selectOpen,
    setSelectOpen,
    selectedUser,
    setSelectedUser,
    options,
    handleAddUsers,
    handleRemoveUser,
    handleUpdateUserRole,
    rolesList,
    owners,
    collaborators,
    isSubmitting,
    isRevoking,
    isUpdating,
    canManageAccess,
    datasetName: dataset.display_name,
  };
};
